import React, { Component, Fragment } from 'react';
import { createMemoryHistory } from 'history';
import PropTypes from 'prop-types';
import AnalyticsSDK, { LOG_LEVELS, SUPPORTED_TARGETS } from '@adsk/bim360-analytics-sdk-js';
import DocsSDKWrapper from './services/docsSDKWrapper';
import { getCurrentToken } from './services/getCurrentToken';
import createRFIsStore from './redux/store';
import { Provider } from 'react-redux';
import RFIsContext, { RFIsRouterContext } from './redux/RFIsContext';
import { ENV_CONFIGS, ISSUES_ENV_TYPES, ISSUES_REGIONS } from './constants/envs';
import { initialize as initializeFF } from './utils/featureFlags';
import { parseQueryString, stringifyQuery } from './utils/query-helpers';
import {
  getAnalyticsMandatoryPeopleProps,
  registerAnalyticsSuperPropsHelper,
  staticMandatorySuperProperties,
} from './utils/analytics-helpers';
import { setLanguage, setLocale } from './utils/i18n';
import Api from './services/api.sdk';
import {
  setFFLoaded,
  configureAnalyticsAction,
  configureJsonApiAction,
  configureJsonApiDocsSDKAction,
  configureWorkflowsApiAction,
  setOxygenEntitiesAction,
  setCurrentProject,
  fetchCurrentUserAction,
  fetchCurrentDocsProjectAction,
  setAnalyticsId,
  registerAnalyticsMandatorySuperProps,
  setAnalyticsMandatoryPeopleProps,
  setCurrentViewAction,
} from './redux/actions';
import { roleType, companyType, oxygenUserType } from './constants/proptype-validation';
import { CONTAINER_TYPES } from './constants/entitlements';
import PushpinsControl from './services/lmv/pushpins-control';
import Redispatcher from './services/redispatcher';
import { viewTypes } from './constants/view-types';
import { Route, Router, Switch } from 'react-router-dom';
import { SVGStore } from '@adsk/bim360-matrix-react-components';
import { SVGs } from '@adsk/bim360-matrix-react-components'

const somePropsUndefined = (props) => {
  const isUndefined = (prop) => typeof prop === 'undefined';
  const {
    env,
    region,
    locale,
    currentProjectEntitlements,
    currentUser,
    users,
    roles,
    companies,
  } = props;

  return [
    env,
    region,
    locale,
    currentProjectEntitlements,
    currentUser,
    users,
    roles,
    companies,
  ].some(isUndefined);
};

class RFIsProvider extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFFLoaded: false,
      ldError: false,
      token: null,
      getTokenError: false,
      initialized: false,
      routerProps: {history: null, location: null},
    };

    if (somePropsUndefined(props)) {
      return null;
    }

    if (props.env.toLowerCase() === 'prod') {
      this.env = ISSUES_ENV_TYPES.PROD
    } else if (props.env.toLowerCase() === 'staging') {
      this.env = ISSUES_ENV_TYPES.STAGING
    } else {
      this.env = props.env;
    }

    this.getState = this.getState.bind(this);
    this.dispatch = this.dispatch.bind(this);
    this.initLocale();
    this.createStore();
    this.initFeatureFlags();
    this.initToken();
  }

  componentDidUpdate() {
    if (
      !this.state.initialized &&
      this.state.isFFLoaded &&
      this.state.token &&
      this.props.users &&
      this.props.companies &&
      this.props.roles
    ) {
      this.initStore();
    }
  }

  dispatch(action) {
    return this.store.dispatch(action);
  }

  getState() {
    return this.store.getState();
  }

  initLocale = () => {
    const { locale } = this.props;

    setLanguage(locale);
    setLocale(locale);
  };

  createStore = () => {
    const { region, locale, currentProjectEntitlements } = this.props;
    const { env } = this;

    // Initialize the analyticsManager
    const analyticsManager = AnalyticsSDK.init({
      moduleName: ENV_CONFIGS[region][env].ANALYTICS.MODULE_NAME,
      targets: { [SUPPORTED_TARGETS.MIXPANEL]: { token: ENV_CONFIGS[region][env].ANALYTICS.MIXPANEL.TOKEN }},
      logLevel: ENV_CONFIGS[region][env].ANALYTICS.ENV.toLowerCase() !== 'prod' ? LOG_LEVELS.INFO : LOG_LEVELS.ERROR,
      mandatorySuperProps: staticMandatorySuperProperties(region, env, locale),
      disableAutoSessionStartEvent: true,
      disableAutoResetSuperProperties: true,
    });

    this.appHistory = createMemoryHistory({
      queryKey: false,
      parseQueryString,
      stringifyQuery,
    });


    this.redispatcher = new Redispatcher();

    const rfiContainer = currentProjectEntitlements.containers.find(
      (container) => container.type === CONTAINER_TYPES.RFIS
    );
    const containerId = rfiContainer.id;

    this.store = createRFIsStore({
      jsonApi: new Api(this.dispatch, this.getState, { env, region, containerId }),
      appHistory: this.appHistory,
      pushpinsControl: null,
      analyticsManager,
      redispatcher: this.redispatcher,
      devToolsCaption: 'RFI Smart Component',
    });
  };

  initFeatureFlags = () => {
    const { region, currentProjectEntitlements, currentUser } = this.props;
    const { env } = this;
    const projectId = currentProjectEntitlements.id;
    const accountId = currentProjectEntitlements.accountId;

    const launchDarklyUserObject = { custom: { region, accountId, projectId }};
    const analyticsId = currentUser.analyticsId;
    if (analyticsId) {
      launchDarklyUserObject.key = analyticsId;
    }

    initializeFF(env, region, launchDarklyUserObject)
      .then(() => {
        this.setState({ isFFLoaded: true });
        this.store.dispatch(setFFLoaded());
      })
      .catch(e => {
        this.setState({ ldError: true });
        // eslint-disable-next-line no-console
        console.error(`Unable to get Launch Darkly response: ${e}`);
      });
  };

  initToken = () => {
    const { region } = this.props;
    const { env } = this;

    getCurrentToken(ENV_CONFIGS[region][env].DOCS)
      .then(token => this.setState({ token }))
      .catch(e => {
        this.setState({ getTokenError: true });
        // eslint-disable-next-line no-console
        console.error(`Unable to get token: ${e}`);
      });
  };

  initStore() {
    const { region, currentProjectEntitlements, users, roles, companies, currentUser, viewType = viewTypes.PROJECT } = this.props;
    const { env } = this;
    const { token } = this.state;

    const rfiContainer = currentProjectEntitlements.containers.find(
      container => container.type === CONTAINER_TYPES.RFIS
    );
    if (!rfiContainer) {
      throw new Error('RFI container is missing in RFIsProvider, make sure you pass RFI contianer to currentProjectEntitlements prop');
    }
    const containerId = rfiContainer.id;

    const reportContainer = currentProjectEntitlements.containers.find(
      container => container.type === CONTAINER_TYPES.REPORT
    );
    if (!reportContainer) {
      throw new Error('Reports container is missing in RFIsProvider, make sure you pass reports contianer to currentProjectEntitlements prop');
    }
    const reportContainerId = reportContainer && reportContainer.id;

    const projectId = currentProjectEntitlements.id;
    const { docsSDK } = DocsSDKWrapper(ENV_CONFIGS[region][env].DOCS_SDK_CONFIG, token);
    const apiConfig = { containerId, reportContainerId, projectId, region, env };
    const analyticsId = currentUser.analyticsId;

    // API configs
    this.store.dispatch(configureJsonApiDocsSDKAction(docsSDK));
    this.store.dispatch(configureJsonApiAction(apiConfig));
    this.store.dispatch(configureWorkflowsApiAction(apiConfig));

    this.store.dispatch(setOxygenEntitiesAction({ users, roles, companies }));
    this.store.dispatch(setCurrentProject(currentProjectEntitlements));

    // Analytics data
    this.store.dispatch(configureAnalyticsAction({ analyticsId }));
    this.store.dispatch(setAnalyticsId(analyticsId));
    // TODO - Check if we actually need to set super and people props (this should already be set by the hosting app)
    this.store.dispatch(registerAnalyticsMandatorySuperProps(
      registerAnalyticsSuperPropsHelper(currentProjectEntitlements, region)
    ));
    this.store.dispatch(setAnalyticsMandatoryPeopleProps(getAnalyticsMandatoryPeopleProps(currentUser)));

    // Fetch relevant data
    this.store.dispatch(fetchCurrentUserAction());
    this.store.dispatch(fetchCurrentDocsProjectAction());

    this.store.dispatch(setCurrentViewAction(viewType));

    this.setState({ initialized: true });
  }

  render() {
    if (somePropsUndefined(this.props)) {
      return null;
    }

    const { Loader, children } = this.props;
    const { initialized } = this.state;
    const isLoader = typeof Loader !== 'undefined';
    if (isLoader && !initialized) return Loader;

    return (
      <Fragment>
        <SVGStore svgs={SVGs} />
        <Provider store={this.store} context={RFIsContext}>
          <Router history={this.appHistory}>
            <Switch>
              <Route path="*" render={(routerProps) => {
                return (
                  <RFIsRouterContext.Provider value={routerProps}>
                    {children}
                  </RFIsRouterContext.Provider>                  
                );
              }}
                />         
            </Switch>
          </Router>
        </Provider>
      </Fragment>
    );
  }
}

RFIsProvider.propTypes = {
  /**
   * The ENV - one of `ISSUES_ENV_TYPES` values
   */
  env: PropTypes.oneOf(Object.values(ISSUES_ENV_TYPES)).isRequired,
  /**
   * The Region - one of `ISSUES_REGIONS` values
   */
  region: PropTypes.oneOf(Object.values(ISSUES_REGIONS)).isRequired,
  /**
   * The locale to use.
   */
  locale: PropTypes.string.isRequired,
  /**
   * Current project entitlements. Should be in the shape of `@adsk/bim360-mc-interface/es/models/EntitlementProjectItem`
   */
  currentProjectEntitlements: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    accountId: PropTypes.string.isRequired,
    accountName: PropTypes.string.isRequired,
    projectScope: PropTypes.arrayOf(PropTypes.string).isRequired,
    containers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  /**
   * The current user
   */
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    analyticsId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Users in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/User`
   */
  users: PropTypes.arrayOf(oxygenUserType).isRequired,
  /**
   * Roles in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Role`
   */
  roles: PropTypes.arrayOf(roleType).isRequired,
  /**
   * Companies in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Company`
   */
  companies: PropTypes.arrayOf(companyType).isRequired,
  /**
   * Component to render while initializing the store
   */
  Loader: PropTypes.node,
  /**
   * The view type of RFI 
   */
  viewType: PropTypes.oneOf(Object.values(viewTypes)).isRequired,
  /**
   * A single node to render as a child
   */
  children: PropTypes.node.isRequired,
};

RFIsProvider.displayName = 'RFIsProvider';

export default RFIsProvider;
